import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Col } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../common/PageHeader";
import Loader from "../../common/Loader";
import SupportLink from "../../root/support/components/SupportLink";
import { findConfigAsProperty, findOneConfig } from "../../../helpers/api/condominium";
import handleErrorMessage from "../../../helpers/errors/handleErrorMessage";
import useFinanceHubData from "./useFinanceHubData";
import { resolveSection, sectionHref, sectionsFor } from "./sections";
import FinanceTabs from "./FinanceTabs";
import FinanceSummary from "./FinanceSummary";
import FinanceCollections from "./FinanceCollections";
import FinanceExpenses from "./FinanceExpenses";
import FinanceSettlement from "./FinanceSettlement";
import FinanceAccountingLinks from "./FinanceAccountingLinks";

const TOO_MANY_REQUESTS = 429;

// Sin días de cobro configurados el backend no puede calcular las finanzas
// del condominio, así que la pantalla se queda en el aviso de configuración.
const useBillingConfig = (condominiumId, role) => {
  const [config, setConfig] = useState({ loading: true, ready: false });

  useEffect(() => {
    if (!condominiumId || !["manager", "property"].includes(role)) return undefined;

    let isMounted = true;
    const request = role === "manager" ? findOneConfig : findConfigAsProperty;

    const load = async () => {
      try {
        const { data } = await request(condominiumId);
        if (isMounted) setConfig({ loading: false, ready: data?.automatic != null });
      } catch (error) {
        if (error?.response?.status === TOO_MANY_REQUESTS) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return load();
        }
        if (isMounted) {
          setConfig({ loading: false, ready: false });
          handleErrorMessage(error);
        }
      }
      return undefined;
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [condominiumId, role]);

  return config;
};

const ConfigMissing = ({ isManager }) => (
  <Alert color="warning" className="d-flex align-items-center shadow-sm">
    <FontAwesomeIcon icon={faExclamationTriangle} size="lg" className="mr-3" />
    <div>
      <h5 className="alert-heading">Falta configurar los días de cobro del condominio</h5>
      <p className="mb-0">
        Las finanzas se calculan a partir de los días de cobro.{" "}
        {isManager ? (
          <>
            Configúralos en <Link to="/dashboard/config">Configuración</Link>.
          </>
        ) : (
          "Pídele al administrador que los configure."
        )}
      </p>
    </div>
  </Alert>
);

// Cantidad que acompaña a una pestaña: lo que hay por hacer dentro de ella.
const badgeFor = (sectionId, data) => {
  if (sectionId === "collections") return data.pendingPays.total;
  if (sectionId === "expenses") return data.pendingDebts.length;
  return 0;
};

// Módulo de Finanzas: reemplaza a las páginas de Finanzas y Contabilidad.
// Cada pestaña muestra sus accesos a la vista, sin listas que desplegar.
const FinanceHub = () => {
  const { search } = useLocation();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { role } = useSelector((state) => state.auth.user);
  const isManager = role === "manager";

  const config = useBillingConfig(selectedCondominium, role);
  const data = useFinanceHubData({
    condominiumId: selectedCondominium,
    role,
    enabled: config.ready,
  });

  const section = resolveSection(search, role);
  const base = `/dashboard/finance/${selectedCondominium}`;
  const sectionProps = { data, base, role, condominiumId: selectedCondominium };

  return (
    <>
      <PageHeader
        title="Finanzas"
        titleTag="h2"
        description={
          isManager
            ? "Saldos, cobros, gastos y el cierre de cada mes de tu condominio."
            : "Consulta las finanzas de tu condominio."
        }
        className="mb-3"
        col={{ lg: 8 }}
        rightDecoration={
          isManager && config.ready ? (
            <Col lg={4} className="d-flex align-items-end justify-content-lg-end mt-3 mt-lg-0">
              <Button
                tag={Link}
                to={sectionHref("settlement")}
                color="primary"
              >
                <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                Planilla del mes
              </Button>
            </Col>
          ) : null
        }
      >
        {isManager && <SupportLink />}
      </PageHeader>

      {config.loading ? (
        <Loader />
      ) : !config.ready ? (
        <ConfigMissing isManager={isManager} />
      ) : (
        <>
          <FinanceTabs
            sections={sectionsFor(role)}
            activeId={section}
            hrefFor={sectionHref}
            badgeFor={(id) => (isManager ? badgeFor(id, data) : 0)}
          />

          <div
            role="tabpanel"
            id={`finance-panel-${section}`}
            aria-labelledby={`finance-tab-${section}`}
          >
            {section === "summary" && <FinanceSummary {...sectionProps} />}
            {section === "collections" && <FinanceCollections {...sectionProps} />}
            {section === "expenses" && <FinanceExpenses {...sectionProps} />}
            {section === "settlement" && <FinanceSettlement {...sectionProps} />}
            {section === "accounting" && isManager && (
              <FinanceAccountingLinks condominiumId={selectedCondominium} />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default FinanceHub;
